import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { motion } from "framer-motion";
import { createBlog, fetchBlogs } from "../../../redux/actions/blogActions";
import {
  FiX,
  FiCheck,
  FiPlusCircle,
  FiType,
  FiGlobe,
  FiUser,
  FiImage,
  FiFileText,
  FiTag,
  FiSearch,
  FiCheckCircle,
} from "react-icons/fi";

const CreateBlog = ({ close }: any) => {
  const dispatch = useDispatch<any>();

  const [formData, setFormData] = useState({
    title: "",
    slug: "",
    short_description: "",
    content: "",
    author: "",
    tags: "",
    meta_title: "",
    status: "published", 
    featured_image: null as File | null,
  });

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleFileChange = (e: any) => {
    setFormData({ ...formData, featured_image: e.target.files[0] });
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    const data = new FormData();

    Object.entries(formData).forEach(([key, value]) => {
      if (value !== null && value !== "") {
        data.append(key, value);
      }
    });

    await dispatch(createBlog(data));
    dispatch(fetchBlogs());
    close();
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-6 sticky top-0 z-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4 text-white">
              <div className="p-2 bg-white/20 rounded-lg">
                <FiPlusCircle className="text-xl" />
              </div>
              <div>
                <h2 className="text-xl font-bold">Create New Blog</h2>
                <p className="text-blue-100 text-sm">
                  Fill in the details to publish a new story
                </p>
              </div>
            </div>
            <button
              onClick={close}
              className="text-white/80 hover:text-white transition-colors"
            >
              <FiX size={24} />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-8 space-y-6">
          {/* Row 1: Title & Slug */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="space-y-1">
              <label className="text-sm font-semibold dark:text-gray-300 flex items-center gap-2">
                <FiType /> Title *
              </label>
              <input
                name="title"
                required
                onChange={handleChange}
                className="w-full p-3 bg-gray-50 dark:bg-gray-700 border dark:border-gray-600 rounded-xl dark:text-white outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g. The Future of AI"
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-semibold dark:text-gray-300 flex items-center gap-2">
                <FiGlobe /> Slug *
              </label>
              <input
                name="slug"
                required
                onChange={handleChange}
                className="w-full p-3 bg-gray-50 dark:bg-gray-700 border dark:border-gray-600 rounded-xl dark:text-white outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="the-future-of-ai"
              />
            </div>
          </div>

          {/* Row 2: Short Description (Crucial for the Blog Cards!) */}
          <div className="space-y-1">
            <label className="text-sm font-semibold dark:text-gray-300 flex items-center gap-2">
              <FiFileText /> Short Description *
            </label>
            <textarea
              name="short_description"
              rows={2}
              required
              onChange={handleChange}
              className="w-full p-3 bg-gray-50 dark:bg-gray-700 border dark:border-gray-600 rounded-xl dark:text-white outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="A brief summary that appears on the blog card..."
            />
          </div>

          {/* Row 3: Main Content */}
          <div className="space-y-1">
            <label className="text-sm font-semibold dark:text-gray-300 flex items-center gap-2">
              <FiFileText /> Content (HTML/Markdown) *
            </label>
            <textarea
              name="content"
              rows={8}
              required
              onChange={handleChange}
              className="w-full p-3 bg-gray-50 dark:bg-gray-700 border dark:border-gray-600 rounded-xl dark:text-white font-mono text-sm outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="<p>Write your full blog content here...</p>"
            />
          </div>

          {/* Row 4: Author, Tags, Status */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <div className="space-y-1">
              <label className="text-sm font-semibold dark:text-gray-300 flex items-center gap-2">
                <FiUser /> Author *
              </label>
              <input
                name="author"
                required
                onChange={handleChange}
                className="w-full p-3 bg-gray-50 dark:bg-gray-700 border dark:border-gray-600 rounded-xl dark:text-white outline-none"
                placeholder="Your Name"
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-semibold dark:text-gray-300 flex items-center gap-2">
                <FiTag /> Tags
              </label>
              <input
                name="tags"
                onChange={handleChange}
                className="w-full p-3 bg-gray-50 dark:bg-gray-700 border dark:border-gray-600 rounded-xl dark:text-white outline-none"
                placeholder="web, ai, tech"
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-semibold dark:text-gray-300 flex items-center gap-2">
                <FiCheckCircle /> Status
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="w-full p-3 bg-gray-50 dark:bg-gray-700 border dark:border-gray-600 rounded-xl dark:text-white outline-none"
              >
                <option value="published">Published</option>
                <option value="draft">Draft</option>
              </select>
            </div>
          </div>

          {/* Row 5: Meta Title & Image */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="space-y-1">
              <label className="text-sm font-semibold dark:text-gray-300 flex items-center gap-2">
                <FiSearch /> Meta Title (SEO)
              </label>
              <input
                name="meta_title"
                onChange={handleChange}
                className="w-full p-3 bg-gray-50 dark:bg-gray-700 border dark:border-gray-600 rounded-xl dark:text-white outline-none"
                placeholder="SEO optimized title"
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2">
                <FiImage className="text-blue-500" /> Featured Image
              </label>
              <input
                type="file"
                onChange={handleFileChange}
                className="block w-full text-sm text-gray-500 dark:text-gray-400
      file:mr-4 file:py-2.5 file:px-6
      file:rounded-full file:border-0
      file:text-sm file:font-bold
      file:bg-blue-50 file:text-blue-700
      dark:file:bg-blue-900/30 dark:file:text-blue-300
      file:cursor-pointer file:transition-all file:duration-300
      hover:file:bg-blue-100 dark:hover:file:bg-blue-900/50
      hover:file:scale-105 hover:file:shadow-md
      active:file:scale-95"
              />
              {formData.featured_image && (
                <div className="flex items-center gap-2 mt-2 ml-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" />
                  <p className="text-[10px] text-blue-600 dark:text-blue-400 font-bold uppercase tracking-wider">
                    Selected: {formData.featured_image.name}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Footer Actions */}
          <div className="flex gap-4 pt-6 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={close}
              className="flex-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 py-3 rounded-xl font-semibold hover:bg-gray-200 dark:hover:bg-gray-600 transition-all duration-200"
            >
              Cancel
            </button>

            {formData.title &&
            formData.slug &&
            formData.content &&
            formData.author ? (
              <motion.button
                initial={{ opacity: 0, scale: 0.9, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                whileHover={{
                  scale: 1.02,
                  shadow: "0 20px 25px -5px rgb(0 0 0 / 0.1)",
                }}
                whileTap={{ scale: 0.95 }} 
                type="submit"
                className="flex-1 bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-3 rounded-xl font-bold shadow-lg shadow-blue-500/30 flex items-center justify-center gap-2 transition-all cursor-pointer"
              >
                <FiCheck className="text-lg" />
                Create Post
              </motion.button>
            ) : (
              <div className="flex-1 bg-gray-100 dark:bg-gray-700/50 text-gray-400 dark:text-gray-500 py-3 rounded-xl font-bold flex items-center justify-center gap-2 border border-dashed border-gray-300 dark:border-gray-600">
                Fill required fields to publish
              </div>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateBlog;
